import { AppContext } from '@/components/AppContextProvider'
import React, { useEffect } from 'react'
import useSegment from '@/hooks/segment/useSegment'
import { useIntersection } from 'react-use'
import { getBreadCrumbsFromRefinementAndrefinementLocation } from '@/domain/segment/convertRefinementsToBreadCrumbs'
import { RefinementList } from '@/domain/search/types'
import generateZoneName from '@/domain/segment/generateZoneName'
import { Center } from '@/types/models'
import { isFacetPageType } from '@/domain/useCases/pageTypeCheck'

type Props = {
  children: React.ReactNode
  position?: number
  positionInElement?: number
  zone: number | string
  center: Center & {
    fromBrowseSearch?: boolean
  }
  refinements?: Partial<RefinementList>
  refinementLocation?: string
}

const SegmentCardTracker = ({
  children,
  position = undefined,
  positionInElement,
  zone,
  center,
  refinements,
  refinementLocation
}: Props): JSX.Element => {
  const { pageType, breadcrumbs } = React.useContext(AppContext)
  const { feed } = useSegment()

  const derivedBreadcrumbs: string =
    getBreadCrumbsFromRefinementAndrefinementLocation({
      refinements,
      center,
      refinementLocation,
      breadcrumbs
    })

  const [impressionTracked, setImpressionTracked] = React.useState(false)

  const ref =
    React.useRef<HTMLElement>() as unknown as React.MutableRefObject<HTMLDivElement>
  const intersection = useIntersection(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  })

  const template = isFacetPageType(pageType) ? 'Facet' : pageType
  const zoneName = generateZoneName({ zone, center })

  const handleClick = (e) => {
    feed.click({
      breadcrumbs: derivedBreadcrumbs,
      position: position,
      positionInElement,
      center: center.slug,
      sponsor_tier: center.sponsor_tier,
      template,
      zone: zoneName,
      action: e?.target?.dataset?.groupName || ''
    })
  }

  const handleImpression = () => {
    if (impressionTracked) return
    feed.impression({
      breadcrumbs: derivedBreadcrumbs,
      position: position,
      positionInElement,
      center: center.slug,
      sponsor_tier: center.sponsor_tier,
      template,
      zone: zoneName
    })

    setImpressionTracked(true)
  }

  useEffect(() => {
    if (intersection?.isIntersecting && !impressionTracked) {
      handleImpression()
    }
  }, [intersection?.isIntersecting])

  return (
    <div
      className="segment-card-tracker"
      onClick={handleClick}
      ref={ref}
    >
      {children}
    </div>
  )
}

export default SegmentCardTracker
